import { useSearchParams } from 'react-router-dom';
import { useMemo, useCallback } from 'react';

/**
 * @hook useEmailHistoryFilters
 * @description Manages URL-based state for Email History filters
 * @returns {Object} Object containing filters, setFilter, and navigateToTab functions
 */
export function useEmailHistoryFilters() {
  const [searchParams, setSearchParams] = useSearchParams();

  // Create filters object directly from searchParams on every render
  // This ensures it always reflects the latest URL params
  // No memoization needed - this is a cheap operation
  const filters = {
    tab: searchParams.get('tab') || 'campaigns',
    search: searchParams.get('search') || '',
    dateFrom: searchParams.get('dateFrom'),
    dateTo: searchParams.get('dateTo'),
    campaignId: searchParams.get('campaignId') ? parseInt(searchParams.get('campaignId'), 10) : null,
    bulkEmailId: searchParams.get('bulkEmailId') ? parseInt(searchParams.get('bulkEmailId'), 10) : null,
    page: parseInt(searchParams.get('page') || '0', 10),
    pageSize: parseInt(searchParams.get('pageSize') || '25', 10),
    sortBy: searchParams.get('sortBy') || 'sendDate',
    sortOrder: searchParams.get('sortOrder') || 'desc',
  };

  /**
   * Set a specific filter value
   * @param {string} key - Filter key
   * @param {*} value - Filter value
   */
  const setFilter = useCallback((key, value) => {
    // Use functional update to get the latest searchParams
    setSearchParams((prevParams) => {
      const newParams = new URLSearchParams(prevParams);
      if (value === null || value === '' || value === undefined) {
        newParams.delete(key);
      } else {
        newParams.set(key, String(value));
      }
      return newParams;
    });
  }, [setSearchParams]);

  /**
   * Navigate to a specific tab with optional additional filters
   * @param {string} tab - Tab name
   * @param {Object} [additionalFilters] - Additional filters to apply
   */
  const navigateToTab = useCallback((tab, additionalFilters = {}) => {
    const newParams = new URLSearchParams();
    newParams.set('tab', tab);
    
    // Apply any additional filters
    Object.entries(additionalFilters).forEach(([key, value]) => {
      if (value !== null && value !== undefined && value !== '') {
        newParams.set(key, String(value));
      }
    });
    
    setSearchParams(newParams);
  }, [setSearchParams]);

  return { filters, setFilter, navigateToTab };
}

